import { CoaTemplateApplySource } from "@common/types/domains/accounting/chartOfAccountsTemplateApplyEvent";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import type { CoaTemplateApplyHistoryEventRef } from "@common/types/auditLog";
import type {
    ChartOfAccountsTemplateApplyEventDetail,
    CoaApplyUndoEligibility,
} from "@common/schemas/domains/accounting/chart-of-accounts-template-apply-event.schema";
import type { HistoryRow } from "../../views/historyViewConfig";
import { canRoleEditCoa } from "@hooks/permissions/canRoleEditCoa";

export type CoaTemplateActivityEventRef = CoaTemplateApplyHistoryEventRef;

export type CoaTemplateActivityEventResult =
    | {
          status: "loaded";
          eventRef: CoaTemplateActivityEventRef;
          detail: ChartOfAccountsTemplateApplyEventDetail;
          eligibility: CoaApplyUndoEligibility;
      }
    | {
          status: "error";
          eventRef: CoaTemplateActivityEventRef;
      };

export function resolveCoaTemplateEventRefs({
    historyRow,
    currentWorkspaceId,
}: {
    historyRow: HistoryRow | null;
    currentWorkspaceId: string | null | undefined;
}): CoaTemplateActivityEventRef[] {
    if (!historyRow) {
        return [];
    }
    const presentation = historyRow.historyItem.presentation;
    if (presentation?.kind === "coaTemplateApply") {
        return presentation.eventRefs;
    }
    // Orphan/generic audit rows carry a null presentation. Preserve the legacy
    // single-resource fallback for them, without synthesizing CoA domain data:
    // only a lone resource/entity in the current workspace resolves to one ref.
    if (
        !currentWorkspaceId ||
        historyRow.resourceIds.length !== 1 ||
        historyRow.entityIds.length !== 1
    ) {
        return [];
    }
    return [
        {
            eventId: historyRow.resourceIds[0],
            entityId: historyRow.entityIds[0],
            workspaceId: currentWorkspaceId,
        },
    ];
}

interface LoadActivityEventsParams {
    eventRefs: CoaTemplateActivityEventRef[];
    loadDetail: (
        eventRef: CoaTemplateActivityEventRef,
    ) => Promise<ChartOfAccountsTemplateApplyEventDetail>;
    loadEligibility: (
        eventRef: CoaTemplateActivityEventRef,
    ) => Promise<CoaApplyUndoEligibility>;
    concurrency?: number;
}

export async function loadCoaTemplateActivityEvents({
    eventRefs,
    loadDetail,
    loadEligibility,
    concurrency = 4,
}: LoadActivityEventsParams): Promise<CoaTemplateActivityEventResult[]> {
    if (eventRefs.length === 0) {
        return [];
    }
    const results: Array<CoaTemplateActivityEventResult | undefined> =
        new Array(eventRefs.length);
    const workerCount = Math.max(1, Math.min(concurrency, eventRefs.length));
    let nextIndex = 0;

    const worker = async () => {
        while (nextIndex < eventRefs.length) {
            const index = nextIndex;
            nextIndex += 1;
            const eventRef = eventRefs[index];
            try {
                const [detail, eligibility] = await Promise.all([
                    loadDetail(eventRef),
                    loadEligibility(eventRef),
                ]);
                results[index] = {
                    status: "loaded",
                    eventRef,
                    detail,
                    eligibility,
                };
            } catch {
                results[index] = { status: "error", eventRef };
            }
        }
    };

    await Promise.all(Array.from({ length: workerCount }, worker));
    return results.filter(isActivityEventResult);
}

function isActivityEventResult(
    result: CoaTemplateActivityEventResult | undefined,
): result is CoaTemplateActivityEventResult {
    return result !== undefined;
}

export function getInitialSelectedEventIds({
    events,
    isReadOnly,
    workspaceRolesById,
}: {
    events: CoaTemplateActivityEventResult[];
    isReadOnly: boolean;
    workspaceRolesById: ReadonlyMap<string, UserWorkspaceRole>;
}): Set<string> {
    if (isReadOnly) {
        return new Set();
    }
    return new Set(
        events
            .filter((event) =>
                isCheckboxCapableEvent({ event, workspaceRolesById }),
            )
            .map((event) => event.eventRef.eventId),
    );
}

// "Checkbox-capable" = selectable in the UI. It is intentionally decoupled from
// backend undo-eligibility: blocked/superseded entities still get a normal
// checkbox and are default-selected so the user sees a disabled Undo with a
// reason. It requires an editable Applied wizard event with a real change set
// that has not already been reverted.
export function isCheckboxCapableEvent({
    event,
    workspaceRolesById,
}: {
    event: CoaTemplateActivityEventResult;
    workspaceRolesById: ReadonlyMap<string, UserWorkspaceRole>;
}): boolean {
    if (event.status !== "loaded") {
        return false;
    }
    const workspaceRole = workspaceRolesById.get(event.eventRef.workspaceId);
    if (!workspaceRole || !canRoleEditCoa(workspaceRole)) {
        return false;
    }
    return (
        event.detail.source === CoaTemplateApplySource.WIZARD &&
        !isChangeSetEmpty(event.detail) &&
        event.detail.revertedAt === null
    );
}

// "Undo-eligible" = the backend can actually undo this entity. Because undo is
// entity-atomic, eligibility is all-or-nothing: superseded or zero-eligible
// events are blocked. The UI only enables the Undo action when every selected
// event is eligible.
export function isUndoEligibleEvent(
    event: CoaTemplateActivityEventResult,
): boolean {
    if (event.status !== "loaded") {
        return false;
    }
    return (
        event.detail.revertedAt === null &&
        !event.eligibility.superseded &&
        event.eligibility.eligibleCount > 0
    );
}

export function isChangeSetEmpty(
    detail: ChartOfAccountsTemplateApplyEventDetail,
): boolean {
    const { changeSet } = detail;
    return (
        changeSet.createdAccounts.length === 0 &&
        changeSet.updatedAccounts.length === 0 &&
        changeSet.createdGroups.length === 0 &&
        changeSet.variableMappingChanges.length === 0
    );
}
